import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { store } from "../../../Redux/Store";
import { attendanceRequestAPIPutwithmuil } from "../../../text/apidata";

// Shared by the attendance regularization and overtime request pages.

const isPending = (row) => row?.status === "pending";

export const useBulkSelection = (rows) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const pendingIds = useMemo(() => (rows || []).filter(isPending).map((r) => r.id), [rows]);

  // A new page, filter or reload shows different rows: start the selection again
  useEffect(() => setSelectedIds([]), [rows]);

  const toggle = (id) =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selectedIds.includes(id));
  const togglePage = () => setSelectedIds(allSelected ? [] : pendingIds);

  return {
    selectedIds,
    pendingIds,
    allSelected,
    someSelected: selectedIds.length > 0 && !allSelected,
    toggle,
    togglePage,
    clear: () => setSelectedIds([]),
  };
};

const checkboxStyle = { width: 17, height: 17, cursor: "pointer", accentColor: "#F05839" };

const HeaderCheckbox = ({ selection }) => (
  <input
    type="checkbox"
    aria-label="Select all pending requests on this page"
    title={selection.pendingIds.length ? "Select all pending on this page" : "No pending requests on this page"}
    style={checkboxStyle}
    disabled={selection.pendingIds.length === 0}
    checked={selection.allSelected}
    ref={(el) => el && (el.indeterminate = selection.someSelected)}
    onChange={selection.togglePage}
  />
);

export const selectionColumn = (selection) => ({
  id: "bulk_select",
  Header: () => <HeaderCheckbox selection={selection} />,
  accessor: "",
  Cell: ({ row }) => {
    const record = row.original;
    return (
      <input
        type="checkbox"
        aria-label="Select request"
        title={isPending(record) ? "" : `Already ${record.status}`}
        style={{ ...checkboxStyle, cursor: isPending(record) ? "pointer" : "not-allowed" }}
        disabled={!isPending(record)}
        checked={selection.selectedIds.includes(record.id)}
        onChange={() => selection.toggle(record.id)}
      />
    );
  },
});

const failureList = (failed) =>
  failed
    .slice(0, 15)
    .map((f) => `<li style="margin-bottom:4px"><b>${f.name || "Request"}</b>: ${f.reason}</li>`)
    .join("") + (failed.length > 15 ? `<li>…and ${failed.length - 15} more</li>` : "");

/**
 * Confirms with the admin, calls the bulk API and reports the result.
 * Resolves true when the list should be reloaded.
 */
export const runBulkAction = async ({ ids, status, label }) => {
  const approve = status === "approved";
  const confirm = await Swal.fire({
    title: `${approve ? "Approve" : "Decline"} ${ids.length} ${label}${ids.length > 1 ? "s" : ""}?`,
    text: approve ? "Each request is approved exactly as with the single Approve button." : undefined,
    input: approve ? undefined : "textarea",
    inputLabel: approve ? undefined : "Reason for decline (applies to all selected)",
    inputPlaceholder: approve ? undefined : "Reason for decline",
    inputValidator: approve ? undefined : (value) => (!value || !value.trim() ? "Please provide a reason for decline" : undefined),
    icon: approve ? "question" : "warning",
    showCancelButton: true,
    confirmButtonColor: "#F05839",
    cancelButtonColor: "#9a9aae",
    confirmButtonText: approve ? "Yes, approve" : "Yes, decline",
  });
  if (!confirm.isConfirmed) return false;

  Swal.fire({ title: "Updating requests…", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
  try {
    const response = await axios.put(
      attendanceRequestAPIPutwithmuil,
      { attendance_id: ids, status, reason_for_decline: approve ? "" : confirm.value.trim() },
      { headers: { Authorization: "Bearer " + store.getState().users.UserData.session.token } }
    );
    const { success_list = [], failed_list = [] } = response.data.data || {};
    if (failed_list.length) {
      await Swal.fire({
        icon: success_list.length ? "warning" : "error",
        title: response.data.status.message,
        html: `<ul style="text-align:left;font-size:14px;padding-left:18px">${failureList(failed_list)}</ul>`,
        confirmButtonColor: "#F05839",
      });
    } else {
      Swal.close();
      toast.success(response.data.status.message, { position: toast.POSITION.TOP_RIGHT });
    }
    return success_list.length > 0;
  } catch (error) {
    Swal.close();
    const status = error?.response?.data?.status;
    if (status?.code === 401) {
      localStorage.clear();
      window.location.href = "/";
      return false;
    }
    toast.error(status?.message || "Could not update the selected requests.", { position: toast.POSITION.TOP_RIGHT });
    return false;
  }
};

export const BulkActionBar = ({ selection, label, onDone }) => {
  const [busy, setBusy] = useState(false);
  const count = selection.selectedIds.length;
  if (!count) return null;

  const act = async (status) => {
    setBusy(true);
    const changed = await runBulkAction({ ids: selection.selectedIds, status, label });
    setBusy(false);
    if (changed) onDone();
  };

  const button = (bg, color) => ({
    background: bg,
    color,
    border: "none",
    borderRadius: 6,
    padding: "7px 16px",
    fontWeight: 600,
    fontSize: 13,
    cursor: busy ? "wait" : "pointer",
    opacity: busy ? 0.6 : 1,
  });

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        background: "#fff4f1",
        border: "1px solid #f8c9bd",
        borderRadius: 8,
        padding: "8px 14px",
        margin: "0 0 10px",
        fontSize: 14,
      }}
    >
      <b>{count} selected</b>
      <span style={{ flex: 1 }} />
      <button type="button" disabled={busy} style={button("#28a745", "#fff")} onClick={() => act("approved")}>
        Approve
      </button>
      <button type="button" disabled={busy} style={button("#dc3545", "#fff")} onClick={() => act("decline")}>
        Decline
      </button>
      <button type="button" disabled={busy} style={button("transparent", "#555")} onClick={selection.clear}>
        Clear
      </button>
    </div>
  );
};
